import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Users, Lock, AlertCircle, Loader2, HeartHandshake } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function AllyRegisterPage(props) {
  const token = props.token;
  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(function () {
    let active = true;
    async function check() {
      try {
        const res = await fetch(API_URL + '/api/ally/invite/' + token);
        const data = await res.json();
        if (!active) return;
        if (!res.ok) {
          setInvalid(data.detail || 'This invitation link is not valid.');
        } else if (!data.valid) {
          setInvalid('This invitation has already been used. Try signing in instead.');
          setInvite(data);
        } else {
          setInvite(data);
        }
      } catch (e) {
        if (active) setInvalid('Could not verify this invitation. Please try again.');
      } finally {
        if (active) setChecking(false);
      }
    }
    check();
    return function () { active = false; };
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(API_URL + '/api/ally/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token: token, password: password })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="ally-register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <HeartHandshake className="h-6 w-6 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-serif font-bold text-foreground">Accountability Ally</h1>
          <p className="text-sm text-muted-foreground mt-1">The Rules of Money Workbook</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 shadow-card">
          {checking ? (
            <div className="py-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : invalid ? (
            <div className="text-center py-4" data-testid="ally-invite-invalid">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-3" />
              <p className="text-sm text-foreground">{invalid}</p>
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{invite.leader_email}</span> invited you to be
                  their accountability ally.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  You'll be able to view their progress and leave check-in notes — but not edit anything.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-sm text-destructive" data-testid="ally-register-error">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4" /> Your Email
                  </Label>
                  <Input value={invite.ally_email} disabled readOnly data-testid="ally-email-display" />
                </div>
                <div>
                  <Label htmlFor="allyPassword" className="flex items-center gap-2 mb-2">
                    <Lock className="h-4 w-4" /> Create Password
                  </Label>
                  <Input
                    id="allyPassword"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={function (e) { setPassword(e.target.value); }}
                    required
                    minLength={6}
                    data-testid="ally-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="allyConfirm" className="flex items-center gap-2 mb-2">
                    <Lock className="h-4 w-4" /> Confirm Password
                  </Label>
                  <Input
                    id="allyConfirm"
                    type="password"
                    placeholder="••••••••"
                    value={confirm}
                    onChange={function (e) { setConfirm(e.target.value); }}
                    required
                    minLength={6}
                    data-testid="ally-confirm-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting} data-testid="ally-register-button">
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Setting up...</>
                  ) : 'Accept & Create Account'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { AllyRegisterPage };
